import React from 'react'
import styled from 'styled-components'
import { CheckCircle2, XCircle } from 'lucide-react'
import {
  colors,
  spacing,
  borderRadius,
  typographyMixin,
  heading,
  body,
  mediaQuery,
  fontWeight,
} from 'src/constants/marketing-ds'
import { COMPARISON_TABLE_DATA, TABLE_FOOTNOTE } from '../data/tableData'

const ComparisonTable = () => {
  return (
    <Section>
      <Container>
        <Title>NBCU Doesn't Negotiate Fairly</Title>
        <Subtitle>
          NBCU is negotiating in bad faith. They are offering terms regarding pricing and packaging
          that are egregiously above those offered to other pay TV distributors. There is no basis
          for this discrimination.
        </Subtitle>
        <TableWrapper>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Feature / Right</TableHeader>
                <TableHeader $center>Other Distributors</TableHeader>
                <TableHeader $center $highlight>
                  Fubo
                </TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {COMPARISON_TABLE_DATA.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.feature}</TableCell>
                  <TableCell $center>
                    {row.otherDistributors ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                  </TableCell>
                  <TableCell $center $highlight>
                    {row.fubo ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrapper>
        <Footnote>{TABLE_FOOTNOTE}</Footnote>
      </Container>
    </Section>
  )
}

export default ComparisonTable

const Section = styled.section`
  padding: ${spacing.xxl} ${spacing.sm};
  background-color: ${colors.white};
  ${mediaQuery.tablet} {
    padding: ${spacing.xxxl} ${spacing.md};
  }
`

const Container = styled.div`
  max-width: 896px;
  margin: 0 auto;
`

const Title = styled.h2`
  ${typographyMixin(heading.md)}
  font-weight: 700;
  margin-bottom: ${spacing.sm};
  text-align: left;
  color: ${colors.slate[60]};

  ${mediaQuery.tablet} {
    ${typographyMixin(heading.lg)}
    text-align: center;
  }
  ${mediaQuery.desktop} {
    ${typographyMixin(heading.xl)}
  }
`

const Subtitle = styled.p`
  color: ${colors.slate[60]};
  max-width: 768px;
  margin: 0 auto ${spacing.lg};
  text-align: left;
  ${typographyMixin(body.md, { fontWeight: fontWeight.normal })}
  line-height: 1.6;
  ${mediaQuery.tablet} {
    text-align: center;
    margin: 0 auto ${spacing.xxxl};
  }
`

const TableWrapper = styled.div`
  overflow: hidden;
  border: 1px solid ${colors.slate[160]};
  border-radius: ${borderRadius.lg};
  background-color: ${colors.white};
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
`

const Table = styled.table`
  width: 100%;
  text-align: left;
  border-collapse: collapse;
`

const TableHead = styled.thead``

const TableRow = styled.tr`
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.slate[190]};
  }
`

const TableHeader = styled.th`
  padding: ${spacing.xxs};
  font-weight: 700;
  color: ${(props) => (props.$highlight ? colors.orange[90] : colors.slate[100])};
  width: ${(props) => (props.$center ? '25%' : '50%')};
  text-align: ${(props) => (props.$center ? 'center' : 'left')};
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.05em;
  background-color: ${(props) => (props.$highlight ? '#fff7ed' : colors.slate[190])};
  border-left: ${(props) => (props.$center ? `1px solid ${colors.slate[160]}` : 'none')};
  ${mediaQuery.tablet} {
    padding: ${spacing.md};
  }
`

const TableBody = styled.tbody`
  & > ${TableRow} {
    border-top: 1px solid ${colors.slate[160]};
  }
`

const TableCell = styled.td`
  padding: ${spacing.xxs};
  color: ${colors.slate[60]};
  font-weight: 500;
  text-align: ${(props) => (props.$center ? 'center' : 'left')};
  border-left: ${(props) => (props.$center ? `1px solid ${colors.slate[160]}` : 'none')};
  background-color: ${(props) => (props.$highlight ? 'rgba(255, 247, 237, 0.5)' : 'transparent')};
  ${mediaQuery.tablet} {
    padding: ${spacing.md};
  }

  svg {
    margin: 0 auto;
    color: ${(props) => {
      if (props.$highlight) return '#ef4444'
      return '#22c55e'
    }};
  }
`

const Footnote = styled.p`
  margin-top: ${spacing.sm};
  text-align: center;
  font-size: 14px;
  color: ${colors.slate[60]};
`
